import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicTitle, BiggerSubtitle } from '@genially/design-system';

const fadeOut = keyframes({
  '0%': {
    opacity: 1,
  },
  '100%': {
    opacity: 0,
  },
});

const withAnimationWrapper = (Component: ElementType) =>
  styled(Component)<{ $activeHideAnimation: boolean }>(
    ({ $activeHideAnimation }) =>
      $activeHideAnimation &&
      css`
        animation: ${fadeOut} 0.625s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
      `,
  );

export const Wrapper = withAnimationWrapper(
  styled.div({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  }),
);

export const ImageWrapper = styled.div({
  position: 'relative',
});

const initialScales = [0.3, 1, 1.5, 2, 3, 4, 5, 6.5, 8, 10, 14];
const endScales = initialScales.slice(1).concat(20);

const initialAngles = [
  '10deg',
  '0deg',
  '-10deg',
  '-20deg',
  '-25deg',
  '-30deg',
  '-40deg',
  '-50deg',
  '-60deg',
  '-70deg',
  '-85deg',
];
const endAngles = initialAngles.slice(1).concat('-99deg');

export const AnimationWrapper = styled.div(() => ({}));

const scaleBoxAnimation = keyframes({
  '0%': {
    transform:
      'translate(-50%, -50%) scale(var(--initial-scale)) rotate(var(--initial-angle))',
  },
  '100%': {
    transform: 'translate(-50%, -50%) scale(var(--end-scale)) rotate(var(--end-angle))',
  },
});

const withScaleBoxAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${scaleBoxAnimation} 2.5s linear infinite;
      `,
  );

interface AnimationItemProps {
  $index: number;
}

export const AnimationItem = withScaleBoxAnimation(
  styled.div<AnimationItemProps>(({ theme: { borderRadius, size }, $index }) => ({
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    position: 'absolute',
    backgroundColor: 'rgba(199, 195, 211, 0.021)',
    backgroundImage: `radial-gradient(
      circle at 50% 10%,
      rgba(18, 18, 18, 0.05) 5%,
      transparent 80%
    ),
    radial-gradient(circle at 50% 90%, rgba(18, 18, 18, 0.05) 25%, transparent)`,
    borderRadius: borderRadius.large,
    boxShadow: `0 0 ${size.units(1)} rgba(0, 0, 0, 0.25)`,
    transform: 'translate(-50%, -50%)',

    '--initial-scale': `${initialScales[$index]}`,
    '--end-scale': `${endScales[$index]}`,
    '--initial-angle': `${initialAngles[$index]}`,
    '--end-angle': `${endAngles[$index]}`,
  })),
);

const scaleDownCenterAnimation = keyframes({
  '0%': {
    opacity: 0,
    transform: 'scale(2)',
  },
  '100%': {
    opacity: 1,
    transform: 'scale(1)',
  },
});

const pulsateAnimation = keyframes({
  '0%': {
    transform: 'scale(1)',
  },
  '50%': {
    transform: 'scale(0.9)',
  },
  '100%': {
    transform: 'scale(1)',
  },
});

const withImageAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean; $phase: boolean }>(
    ({ $activeAnimation, $phase }) => {
      if ($activeAnimation) {
        if ($phase === 0) {
          return css`
            animation: ${scaleDownCenterAnimation} 0.25s
              cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
            animation-delay: 0.3125s;
          `;
        }

        return css`
          animation: ${pulsateAnimation} 0.5s ease-in-out infinite both;
        `;
      }

      return {};
    },
  );

export const StyledImage = withImageAnimation(
  styled.img(({ theme: { size } }) => ({
    position: 'relative',
    width: size.units(22.5),
  })),
);

const trackingInContractAnimation = keyframes({
  '0%': {
    letterSpacing: '1em',
    opacity: 0,
  },
  '40%': {
    opacity: 0.6,
  },
  '100%': {
    letterSpacing: 'normal',
    opacity: 1,
  },
});

const withTitleAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${trackingInContractAnimation} 0.35s
          cubic-bezier(0.215, 0.61, 0.355, 1) both;
        animation-delay: 0.8125s;
      `,
  );

export const StyledBasicTitle = withTitleAnimation(
  styled(BasicTitle)(({ theme: { color } }) => ({
    position: 'relative',
    color: color.content.reversed.default(),
    whiteSpace: 'nowrap',
    textAlign: 'center',
  })),
);

const slideInTopAnimation = keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateY(-100%)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const withSubtitleAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInTopAnimation} 0.3125s cubic-bezier(0.25, 0.46, 0.45, 0.94)
          both;
        animation-delay: 1.1625s;
      `,
  );

export const StyledBiggerSubtitle = withSubtitleAnimation(
  styled(BiggerSubtitle)(({ theme: { color, size } }) => ({
    position: 'relative',
    color: color.content.reversed.default(),
    textAlign: 'center',
    padding: size.units(1.5),
  })),
);
